import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import LanguageSwitcher from "../components/LanguageSwitcher";

export default function ResetPassword() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { token } = useParams();

  useEffect(() => {
    if (!token) {
      setApiMessage(t("invalidToken"));
    }
  }, [token, t]);

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("minPassword", { count: 8 }))
        .max(20, t("field_max_20"))
        .required(t("required")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t("passwordsMustMatch"))
        .required(t("required")),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      if (!token) return;

      setLoading(true);
      setApiMessage("");
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/reset-password`,
          { token, password: values.password, password_confirmation: values.confirmPassword },
          { headers: { lang: i18n.language } }
        );

        setApiMessage(response.data.message || t("passwordResetSuccess"));
        setTimeout(() => navigate("/auth/login"), 1500);
      } catch (error) {
        if (error.response) {
          setApiMessage(error.response.data.message || t("somethingWentWrong"));
        } else {
          setApiMessage(t("networkError"));
        }
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <div
      className="flex min-h-screen flex-col bg-gray-100 text-sm text-[#616262]"
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      {/* Top bar */}
      <div className="border-b border-gray-200 bg-white py-4">
        <div className="z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-6">
          <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-start">
            {/* Logo */}
            <img
              onClick={() => navigate("/auth/landing")}
              src="/logo-blue.svg"
              alt="Logo"
              className="h-10 w-auto cursor-pointer sm:h-8 ltr:pr-3 rtl:pl-3"
            />
          </div>
          {/* <LanguageSwitcher defaultTheme /> */}
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-lg">
          <h1 className="mb-4 text-center text-2xl font-bold text-[#616262]/90">{t("resetPassword")}</h1>

          {apiMessage && (
            <div
              className={`mb-4 rounded-lg px-4 py-2 ${
                apiMessage.toLowerCase().includes("success") || apiMessage.includes("بنجاح")
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {apiMessage}
            </div>
          )}

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
            <div className="space-y-3">
              {/* New Password */}
              <div className="relative">
                <FaLock className="absolute top-3 text-[#616262] ltr:left-3 rtl:right-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("newPassword")}
                  className={`w-full rounded-xl border-2 px-10 py-3 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.password && formik.errors.password
                      ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                      : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                  }`}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-4 text-[#616262] ltr:right-3 rtl:left-3"
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <FaLock className="absolute top-3 text-[#616262] ltr:left-3 rtl:right-3" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder={t("confirmNewPassword")}
                  className={`w-full rounded-xl border-2 px-10 py-3 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.confirmPassword && formik.errors.confirmPassword
                      ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                      : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                  }`}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute top-4 text-[#616262] ltr:right-3 rtl:left-3"
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || formik.isSubmitting || !token}
              className="w-full rounded-full bg-purple-500 py-3 text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:bg-purple-600"
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
                </div>
              ) : (
                t("resetPassword")
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate("/auth/login")}
              className="text-base font-semibold text-purple-500 hover:underline"
            >
              {t("backToLogin")}
            </button>
          </div>
        </div>
      </div>
      {/* Footer Logos */}
      <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-white py-1">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
            <img
              src="/Modee2.png"
              alt="Modee"
              className="h-16 w-[75px] object-contain opacity-90 transition hover:opacity-100 sm:h-20"
            />
          </a>

          <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
            <img
              src="/CPF2.png"
              alt="CPF"
              className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
            />
          </a>
          <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
            <img
              src="/YTJ2.png"
              alt="YTJ"
              className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
            />
          </a>
        </div>
        <div>
          <p className="text-center text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Designed and developed by HWK
          </p>
        </div>
      </footer>
    </div>
  );
}
